import type { DocPage } from "../types";

export const page: DocPage = {
  slug: "index",
  title: "Mavori documentation",
  navLabel: "Overview",
  description:
    "Learn how Mavori brings research, strategies, portfolio context, and supervised trading into one AI agent.",
  section: "start",
  published: "2026-08-03",
  updated: "2026-08-03",
  targets: ["Mavori documentation", "Mavori product overview", "how to use Mavori"],
  related: ["getting-started", "agent", "strategies", "accounts"],
  blocks: [
    {
      type: "lead",
      text: "Mavori is a hosted trading agent for researching markets, building and testing strategies, understanding a portfolio, and preparing actions for your review. These docs explain the product by feature and by the flows you complete in the app.",
    },

    { type: "h2", text: "One workflow from question to decision" },
    {
      type: "p",
      text: "Start in chat with a company, market, portfolio, or strategy question. The agent gathers current evidence, shows the work it used, and returns an answer you can inspect. From there you can continue into a company report, a saved file, a strategy, a backtest, or a proposed trade without rebuilding the context in separate tools.",
    },
    {
      type: "p",
      text: "When research continues into a Strategy and a trade, the flow can become **research → verify → backtest when supported → decide → propose → approve → review**. You can stop at any stage, and research or strategy authoring does not require a connected brokerage account.",
    },

    { type: "h2", text: "The main product areas" },
    {
      type: "table",
      caption: "Where each kind of work lives",
      headers: ["Area", "Use it for"],
      rows: [
        ["Agent", "Research, analysis, follow-up questions, files, and specialist skills"],
        ["Markets", "Company reports, news, earnings, technicals, macro context, and screening"],
        ["Strategies", "Creating, editing, backtesting, and deploying executable strategies"],
        ["Portfolio", "Live, manual, and paper accounts, positions, risk controls, and history"],
        ["Scheduled", "Recurring Agent work and run history"],
        ["Alerts and Inbox", "Monitored conditions, approval requests, and items that need you"],
        ["MCP", "Using Mavori from another supported AI client"],
      ],
    },

    { type: "h2", text: "Supervision stays with you" },
    {
      type: "p",
      text: "Mavori separates analysis from financial action. A broker order is presented as a specific proposal for your approval, and account-level [Risk controls](/docs/trading) are checked before placement. Strategy deployment is also an explicit action. Market data can be delayed or incomplete, and the product preserves those states instead of presenting missing coverage as certainty.",
    },

    { type: "h2", text: "Choose a starting point" },
    {
      type: "ul",
      items: [
        "Follow [Getting started](/docs/getting-started) for a short first session.",
        "Read [Using the agent](/docs/agent) to understand chat, evidence, and follow-ups.",
        "Open [Strategies](/docs/strategies) to turn a market rule into something testable.",
        "Open [Portfolio accounts](/docs/accounts) before connecting a broker or creating a paper account.",
      ],
    },
  ],
};
